import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {
    markMinorConversions,
    usePublishAnalyticsEvent,
} from "@khan/analytics-learning-time";
import {
    isDateConsideredOverAgeGate,
    LoginButton,
    type Role,
    type UpdateStep,
} from "@khan/auth-shared";
import {SignupOauthButtons} from "@khan/auth-signup";
import {BirthdayPicker} from "@khan/component-birthday-picker";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import sharedStyles from "../shared-styles.ts";
import styleConstants from "../style-constants.ts";

import EnvelopeIcon from "./envelope-icon.tsx";

type Props = {
    /**
     * If supplied, the class code to associate with the user after sign up.
     */
    classCode?: string;
    /**
     * The desired URL to redirect to after sign up. Note: this could be
     * overridden by calculateNextUrl
     */
    continueUrl?: string;
    /**
     * The role of the user. Used to display the correct form fields. Will be
     * set on user profile after sign up.
     */
    role?: Role;
    /**
     * The user's birthday. Used to display the correct form fields. Will be
     * set on user profile after sign up.
     */
    birthdate?: string;
    /**
     * Handler for when the user's birthday is changed.
     */
    onBirthdateChange: (birthdate: string, under13: boolean) => void;
    /**
     * Used by ProgressShell to coordinate which screens to render.
     */
    updateStep: UpdateStep;
    /**
     * Whether to show the Facebook sign up button.
     */
    showFacebookButton?: boolean;
    /**
     * The size of the buttons.
     */
    size?: "large" | "medium" | "small";
    /**
     * Whether to only show the icon and not the text. The list will display
     * horizontally.
     */
    iconOnly?: boolean;
};

/**
 * A form for collecting a user's birthday and showing sign up buttons.
 * For login buttons, see oauth-buttons.tsx
 */
const BirthdayAndSignupSubform = (props: Props): JSX.Element => {
    const {role = "learner", showFacebookButton = true, size = "large"} = props;

    const [birthdate, setBirthdate] = React.useState(props.birthdate || "");
    const [serverError, setServerError] = React.useState<
        string | null | undefined
    >(null);

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    React.useEffect(() => {
        const birthdateChanged = props.birthdate !== birthdate;
        if (birthdateChanged && birthdate) {
            publishAnalyticsEvent(
                {
                    eventType: "SignupBirthdayFilled",
                    eventSchemaVersion: 1,
                    isIOS: RequestInfo.isIOS,
                },
                // NOTE: we cannot add the legacy bingo conversion id here
                // it's a minor conversion.
            );
            markMinorConversions([
                {
                    id: "signup_birthdate_filled",
                    extra: {
                        isIOS: RequestInfo.isIOS,
                    },
                },
            ]);
        }
        // NOTE(kevinb): We only care about tracking changes to the birtdate.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [birthdate, props.birthdate]);

    const updateBirthdateFromDatepicker = (birthdate?: string | null) => {
        setBirthdate(birthdate || "");
    };

    const handleEmailClick = React.useCallback(
        (ev: React.SyntheticEvent) => {
            const under13 = !isDateConsideredOverAgeGate(birthdate, role);
            publishAnalyticsEvent(
                {
                    eventType: "SignupWithEmailButtonClicked",
                    eventSchemaVersion: 1,
                    isUnder13: under13,
                },
                // NOTE: we cannot add the legacy bingo conversion id here
                // it's a minor conversion.
            );
            markMinorConversions([{id: "signup_email_button_clicked"}]);

            props.onBirthdateChange(birthdate, under13);
            props.updateStep("identify");
        },
        [birthdate, props, role, publishAnalyticsEvent],
    );

    const showSignupButtons = React.useCallback(() => {
        // non-learners always have signup buttons,
        // learners need a valid birthdate
        return role !== "learner" || !!birthdate;
    }, [role, birthdate]);

    const SignupButtons = (): null | React.ReactElement<
        React.ComponentProps<typeof View>
    > => {
        const {continueUrl, classCode, iconOnly} = props;
        const under13 = !isDateConsideredOverAgeGate(birthdate, role);

        return (
            <>
                <ul
                    className={css(
                        styles.buttons,
                        iconOnly ? styles.buttonsIconOnly : null,
                    )}
                >
                    <SignupOauthButtons
                        under13={under13}
                        role={role}
                        continueUrl={continueUrl}
                        classCode={classCode}
                        iconOnly={iconOnly}
                        size={size}
                        setServerError={setServerError}
                        showFacebookButton={showFacebookButton}
                        birthdate={birthdate}
                    />
                    {/* If we're treating the user as under-13, we'll add a generic
            "continue" message to this button to de-emphasize the need for
            an e-mail address (that will actually be their parent's). The
            functionality of the button is the same either way. */}
                    <ListItem iconOnly={iconOnly}>
                        <LoginButton
                            title={
                                under13
                                    ? t`Sign up by choosing a username`
                                    : t`Sign up with Email`
                            }
                            testId="sign-up-with-email-button"
                            size="large"
                            iconComponent={under13 ? null : EnvelopeIcon}
                            onClick={handleEmailClick}
                            iconOnly={iconOnly}
                        />
                    </ListItem>
                </ul>
                {serverError && (
                    <Body tag="p" role="alert" style={sharedStyles.fieldError}>
                        {serverError}
                    </Body>
                )}
            </>
        );
    };

    return (
        <View>
            <View
                style={
                    role === "learner"
                        ? styles.birthdayPickerWrapper
                        : styles.invisibleBirthdayPickerWrapper
                }
            >
                <View style={sharedStyles.label}>
                    <View style={styles.birthdayPickerPrompt}>
                        {t`What is your date of birth?`}
                    </View>

                    <BirthdayPicker
                        defaultValue={birthdate}
                        onChange={updateBirthdateFromDatepicker}
                    />
                </View>
            </View>
            {showSignupButtons() && <SignupButtons />}
        </View>
    );
};

/**
 * Styled component for the oauth button list.
 */
function ListItem({
    iconOnly,
    children,
}: {
    iconOnly?: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <li
            className={css(
                sharedStyles.signupButtonWrapper,
                styles.listItem,
                iconOnly ? styles.listItemIconOnly : null,
            )}
        >
            {children}
        </li>
    );
}

const styles = StyleSheet.create({
    birthdayPickerWrapper: {
        display: "block",
        minHeight: 95,
        [mediaQueries.xsOrSmaller]: {
            marginBottom: spacing.large_24,
        },
    },
    invisibleBirthdayPickerWrapper: {
        height: 0,
        opacity: 0,
        visibility: "hidden",
        overflow: "hidden",
    },
    birthdayPickerPrompt: {
        fontWeight: "normal",
        marginBottom: 4,
    },
    buttons: {
        // @ts-expect-error - TS2322 - Type '{ from: { opacity: number; }; to: { opacity: number; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            from: {opacity: 0},
            to: {opacity: 1},
        },
        animationDuration: "500ms",
    },
    buttonsIconOnly: {
        display: "grid",
        gridAutoFlow: "column",
        gap: 16,
    },
    listItem: {
        marginBottom: spacing.xxxSmall_4,
    },
    listItemIconOnly: {
        marginBottom: 0,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    login: {
        marginTop: 24,
        marginBottom: 72,
        textAlign: "center",
        fontSize: styleConstants.BODY_FONT_SIZE,
        color: color.green,
    },
    // eslint-disable-next-line react-native/no-unused-styles
    link: {
        ":hover": {
            textDecoration: "none",
            color: color.teal,
        },
    },
});

export default BirthdayAndSignupSubform;
